// sound5/scene.js: the adaptive score as a pure function of the film state.
// No Web Audio here, so it can be unit-tested in Node.
//
// targets(st, m) -> levels (0..1) and parameters for every layer. The engine
// eases each one with its own time constant, so hard switches here become
// crossfades. `m` is the engine's memory: { lit, sinceIgnite, silentIgnite,
// sinceBrand, dark } (times in seconds on the audio clock, -1 = never).
import { clamp, lerp, smoothstep as ss } from './util.js';

// GRAVITY: camera radius (in M = GM/c^2; the horizon is at r = 2) against
// chapter progress. Keys copied from worlds/blackhole/rig.js (LOGR) so the
// redshift you hear follows the fall you see.
const R_KEYS = [
  [0.0, 130], [0.1, 92], [0.22, 56], [0.34, 40], [0.6, 36], [0.68, 34], [0.76, 20],
  [0.83, 10], [0.88, 5.5], [0.92, 3.2], [0.96, 2.3], [1.0, 2.012],
];

// Monotone cubic Hermite (Fritsch-Carlson), as in rig.js, on log r.
function curve(keys) {
  const n = keys.length, xs = keys.map((k) => k[0]), ys = keys.map((k) => k[1]);
  const d = [], m = new Array(n).fill(0);
  for (let i = 0; i < n - 1; i++) d.push((ys[i + 1] - ys[i]) / (xs[i + 1] - xs[i]));
  m[0] = d[0]; m[n - 1] = d[n - 2];
  for (let i = 1; i < n - 1; i++) m[i] = d[i - 1] * d[i] <= 0 ? 0 : (d[i - 1] + d[i]) / 2;
  for (let i = 0; i < n - 1; i++) {
    if (d[i] === 0) { m[i] = 0; m[i + 1] = 0; continue; }
    const a = m[i] / d[i], b = m[i + 1] / d[i], s = a * a + b * b;
    if (s > 9) { const t = 3 / Math.sqrt(s); m[i] = t * a * d[i]; m[i + 1] = t * b * d[i]; }
  }
  return (x) => {
    if (x <= xs[0]) return ys[0];
    if (x >= xs[n - 1]) return ys[n - 1];
    let i = 0;
    while (x > xs[i + 1]) i++;
    const h = xs[i + 1] - xs[i], t = (x - xs[i]) / h, t2 = t * t, t3 = t2 * t;
    return (2 * t3 - 3 * t2 + 1) * ys[i] + (t3 - 2 * t2 + t) * h * m[i] + (-2 * t3 + 3 * t2) * ys[i + 1] + (t3 - t2) * h * m[i + 1];
  };
}
const LOGR = curve(R_KEYS.map(([p, r]) => [p, Math.log(r)]));

// Gravitational redshift seen from far away: f_obs / f_emit = sqrt(1 - 2M/r).
// The same factor stretches time (the clock ticks slow down by 1/f).
export function redshift(p) {
  const r = Math.exp(LOGR(clamp(p))), f = Math.sqrt(Math.max(1e-4, 1 - 2 / r));
  return { r, f, cents: Math.max(-2400, 1200 * Math.log2(f)), dilation: 1 / f };
}

export function blank() {
  return {
    prog: null, hold: false,
    organ: 0, bright: 2400, choir: 0, vowel: 0.3, pad: 0, sparkle: 0,
    piano: 0, thin: 1, // piano: IMPORTANCE threshold, lower = more notes
    drone: 0, droneCut: 110, droneRise: 0, cloud: 0,
    cluster: 0, clusterCut: 400, clusterRise: 0, trem: 0,
    heart: 0, bpm: 60, tick: 0, tickEvery: 1.25,
    bend: 0, dim: 1, air: 0, master: 1, dip: 0, pan: 0, tilt: 0,
  };
}

export function targets(st, m) {
  const T = blank();
  const c = st.chapter, p = st.progress;
  T.dip = st.transition;
  T.pan = clamp(st.look.x, -1, 1) * 0.35;
  T.tilt = clamp(st.look.y, -1, 1);
  T.air = m.lit ? ss(0.15, 2.5, Math.abs(st.velocity)) * 0.6 : 0;
  if (m.dark) { T.air = 0; T.master = 0; return T; } // after the horizon: silence

  if (c === 0 && !m.lit) {
    // IGNITE, before the star: a dark sub drone; holding tightens everything
    const q = st.charge;
    T.drone = 0.18 + 0.67 * q;
    T.droneCut = 120 + 1500 * q * q;
    T.droneRise = 700 * q;
    T.cloud = 0.3 + 0.6 * q;
    T.cluster = ss(0.03, 0.5, q);
    T.clusterCut = 300 + 4200 * q * q;
    T.clusterRise = 100 * q;
    T.trem = ss(0.3, 1, q);
    T.heart = ss(0.06, 0.4, q);
    T.bpm = 54 + 78 * q;
    return T;
  }
  if (c === 0) {
    // the bloom after ignition (starts when the reverse swell lands)
    const s = m.sinceIgnite;
    T.prog = m.silentIgnite || s >= 1.95 ? 'bloom' : null;
    T.organ = 0.9;
    T.bright = 2200 + 1800 * ss(0, 1, p);
    T.pad = 0.55 * (m.silentIgnite ? 1 : ss(1.5, 5, s));
    T.sparkle = 0.3 + 0.3 * ss(0, 0.6, p);
    T.choir = 0.18 * ss(0.4, 1, p);
    T.vowel = 0.5;
    return T;
  }
  if (c === 1) {
    // HOME: orbital sunrise, your city at night, the pale blue dot
    const sun = ss(0, 0.28, p), city = ss(0.3, 0.45, p) * (1 - ss(0.68, 0.8, p)), dot = ss(0.72, 0.92, p);
    T.prog = 'home';
    T.organ = 0.62 + 0.33 * sun - 0.3 * dot;
    T.bright = 1300 + 3000 * sun - 1300 * dot - 600 * city;
    T.piano = (0.25 + 0.6 * ss(0.08, 0.32, p)) * (1 - 0.45 * dot);
    T.thin = clamp(lerp(0.8, 0.28, city) + 0.4 * dot, 0.25, 0.95);
    T.choir = 0.1 + 0.3 * dot;
    T.vowel = 0.2;
    T.pad = 0.18 + 0.25 * dot;
    T.sparkle = 0.1 + 0.2 * sun * (1 - city) + 0.15 * dot;
    return T;
  }
  if (c === 2) {
    // SCALE: vast, slow; choir and shimmer open up as we rise out of the disc
    T.prog = 'scale';
    T.choir = 0.62 + 0.38 * ss(0, 0.6, p);
    T.vowel = ss(0.1, 0.8, p);
    T.organ = 0.45 + 0.3 * ss(0.2, 0.9, p);
    T.bright = 1500 + 3200 * ss(0.2, 0.9, p);
    T.pad = 0.75;
    T.sparkle = 0.4 + 0.3 * p;
    // a faint D1 pedal under the galaxy: vastness, and no hole in the low end after HOME
    T.drone = 0.07;
    T.droneCut = 90;
    return T;
  }
  if (c === 3) {
    // GRAVITY: heavy and slow; everything redshifts as we fall
    const z = redshift(p);
    T.prog = 'gravity';
    T.organ = 0.55 + 0.4 * ss(0, 0.3, p);
    T.bright = 750 + 1500 * ss(0.05, 0.35, p) - 1100 * ss(0.7, 0.95, p);
    T.drone = 0.52 + 0.2 * ss(0.6, 0.9, p);
    T.droneCut = 200 + 300 * ss(0.3, 0.7, p); // enough saw harmonics that phone speakers hear the weight
    T.cloud = 0.25;
    T.tick = 0.6 * ss(0.03, 0.12, p);
    T.tickEvery = Math.min(6, 1.25 * z.dilation);
    T.choir = 0.3 * ss(0.25, 0.4, p) * (1 - ss(0.8, 0.95, p));
    T.vowel = 0;
    T.bend = z.cents;
    T.dim = Math.pow(clamp(z.f, 0.25, 1), 0.8);
    return T;
  }
  if (c === 4) {
    // YOURS: out of the dark; the brand's star (and its motif) is born
    const b = m.sinceBrand >= 0 ? ss(0, 2.5, m.sinceBrand) : 0;
    T.prog = 'yours';
    T.organ = 0.45 + 0.4 * b;
    T.bright = 1700 + 1900 * b + 600 * p;
    T.pad = 0.35 + 0.3 * b;
    T.sparkle = 0.12 + 0.3 * b;
    T.choir = 0.15;
    T.vowel = 0.6;
    return T;
  }
  // OFFER: resolve, then quiet
  T.prog = 'offer';
  T.hold = p > 0.8;
  T.organ = lerp(0.75, 0.32, ss(0.45, 1, p));
  T.bright = 2400 - 1200 * ss(0.6, 1, p);
  T.piano = 0.6 * (1 - ss(0.62, 0.85, p));
  T.thin = 0.5;
  T.pad = 0.22 * (1 - ss(0.7, 1, p));
  T.sparkle = 0.12 * (1 - ss(0.6, 0.9, p));
  T.master = lerp(1, 0.62, ss(0.8, 1, p));
  return T;
}
